import { Fragment, useEffect, useState } from 'react';
import { getMatchPlayerScore, getPlayerId } from '../../utils/bracket';
import { useI18n } from '../../i18n';

const MAX_SCORE = 999;
const MAX_ROUNDS = 10;
const FIELDS = ['head', 'body', 'total'];

function toInput(value) {
  return value == null ? '' : String(value);
}

function emptySide() {
  return { head: '', body: '', total: '', totalTouched: false };
}

function toSide(score) {
  if (!score) return emptySide();
  return {
    head: toInput(score.head),
    body: toInput(score.body),
    total: toInput(score.total),
    // 저장된 최종점수가 머리+몸통과 다르면 직접 입력한 값으로 본다
    totalTouched: score.total != null && score.total !== (score.head ?? 0) + (score.body ?? 0),
  };
}

/** 입력 행: [{ sides: [선수1, 선수2] }]. 라운드 번호는 행 순서(1부터) */
function initialRows(match) {
  const ids = [match.player1, match.player2].map((p) => String(getPlayerId(p)));
  const saved = [...(match.roundScores ?? [])].sort((a, b) => a.round - b.round);
  if (saved.length > 0) {
    return saved.map((r) => ({ sides: ids.map((id) => toSide(r.scores?.[id])) }));
  }
  // 라운드 없이 합계만 저장된 예전 점수는 1라운드로 불러온다
  const legacy = [match.player1, match.player2].map((p) => getMatchPlayerScore(match, p));
  if (legacy.some(Boolean)) {
    return [{ sides: legacy.map(toSide) }];
  }
  return [{ sides: [emptySide(), emptySide()] }];
}

function parseScore(value) {
  if (value === '') return null;
  const number = Number(value);
  return Number.isInteger(number) ? number : NaN;
}

function autoTotal(side) {
  const head = parseScore(side.head);
  const body = parseScore(side.body);
  if (head == null && body == null) return '';
  if (Number.isNaN(head) || Number.isNaN(body)) return side.total;
  return String((head ?? 0) + (body ?? 0));
}

function sumColumn(rows, sideIndex, field) {
  let sum = 0;
  let any = false;
  rows.forEach((row) => {
    const value = parseScore(row.sides[sideIndex][field]);
    if (value != null && !Number.isNaN(value)) {
      sum += value;
      any = true;
    }
  });
  return any ? sum : '-';
}

/**
 * 경기 점수 입력: 라운드별 머리/몸통/최종점수 (라운드 수는 경기마다 다를 수 있음).
 * 승자는 자동으로 정하지 않고 관리자가 직접 고른다. allowDraw(1:1 경기)면 무승부도 고를 수 있다.
 */
const DRAW = 'draw';

function MatchScoreModal({ open, match, title, subtitle, allowDraw = false, onSave, onClose }) {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [winnerIndex, setWinnerIndex] = useState(null);
  const [error, setError] = useState('');

  const players = match ? [match.player1, match.player2] : [];

  useEffect(() => {
    if (!open || !match) return;
    setRows(initialRows(match));
    const savedWinner = [match.player1, match.player2].findIndex(
      (p) => p && match.winner && getPlayerId(p) === getPlayerId(match.winner)
    );
    setWinnerIndex(allowDraw && match.draw ? DRAW : savedWinner >= 0 ? savedWinner : null);
    setError('');
  }, [open, match, allowDraw]);

  if (!open || !match || rows.length === 0) return null;

  const updateCell = (rowIndex, sideIndex, field, value) => {
    if (value !== '' && !/^\d{0,3}$/.test(value)) return;
    setRows(
      rows.map((row, r) => {
        if (r !== rowIndex) return row;
        const sides = row.sides.map((side, i) => {
          if (i !== sideIndex) return side;
          const updated = { ...side, [field]: value };
          if (field === 'total') {
            updated.totalTouched = value !== '';
          } else if (!side.totalTouched) {
            updated.total = autoTotal(updated);
          }
          return updated;
        });
        return { sides };
      })
    );
  };

  const handleAddRound = () => {
    if (rows.length >= MAX_ROUNDS) {
      setError(t('bracket.scoreModal.maxRounds', { max: MAX_ROUNDS }));
      return;
    }
    setError('');
    setRows([...rows, { sides: [emptySide(), emptySide()] }]);
  };

  const handleRemoveRound = (rowIndex) => {
    const next = rows.filter((_, r) => r !== rowIndex);
    setRows(next.length > 0 ? next : [{ sides: [emptySide(), emptySide()] }]);
  };

  const handleClearScores = () => {
    setRows([{ sides: [emptySide(), emptySide()] }]);
    setWinnerIndex(null);
    setError('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const ids = players.map((p) => String(getPlayerId(p)));
    const roundScores = [];
    for (const row of rows) {
      const scores = {};
      for (let i = 0; i < 2; i += 1) {
        const values = FIELDS.map((field) => parseScore(row.sides[i][field]));
        if (values.some((v) => Number.isNaN(v) || (v != null && (v < 0 || v > MAX_SCORE)))) {
          setError(t('bracket.scoreModal.invalidScore', { max: MAX_SCORE }));
          return;
        }
        if (values.some((v) => v != null)) {
          const [head, body, total] = values;
          scores[ids[i]] = {
            head: head ?? 0,
            body: body ?? 0,
            total: total ?? (head ?? 0) + (body ?? 0),
          };
        }
      }
      // 빈 행은 건너뛰고, 라운드 번호는 입력된 행 순서대로 1부터 매긴다
      if (Object.keys(scores).length > 0) {
        roundScores.push({ round: roundScores.length + 1, scores });
      }
    }
    const draw = winnerIndex === DRAW;
    onSave({
      roundScores,
      winner: winnerIndex != null && !draw ? players[winnerIndex] : null,
      draw,
    });
  };

  const fieldLabels = {
    head: t('bracket.scoreModal.head'),
    body: t('bracket.scoreModal.body'),
    total: t('bracket.scoreModal.total'),
  };
  const playerName = (player) => player.participantName || player.name;

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-dialog modal-dialog-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="match-score-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <p className="modal-eyebrow">MATCH SCORE</p>
            <h2 id="match-score-title" className="modal-title">
              {title}
            </h2>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="score-rounds-wrap">
            <table className="score-rounds">
              <thead>
                <tr>
                  <th rowSpan={2} className="score-rounds-round">
                    {t('bracket.scoreModal.round')}
                  </th>
                  {players.map((player, index) => (
                    <th
                      key={getPlayerId(player)}
                      colSpan={3}
                      className={`score-rounds-player${
                        winnerIndex === index ? ' score-rounds-winner' : ''
                      }`}
                    >
                      <span className="score-side-name">{playerName(player)}</span>
                      <span className="score-side-gym">{player.gymName ?? t('common.noGym')}</span>
                      <label className="score-winner-option">
                        <input
                          type="radio"
                          name="match-winner"
                          checked={winnerIndex === index}
                          onChange={() => setWinnerIndex(index)}
                        />
                        {t('bracket.scoreModal.win')}
                      </label>
                    </th>
                  ))}
                  <th rowSpan={2} aria-hidden="true" />
                </tr>
                <tr>
                  {players.map((player) => (
                    <Fragment key={getPlayerId(player)}>
                      {FIELDS.map((field) => (
                        <th key={field} className="score-rounds-field">
                          {fieldLabels[field]}
                        </th>
                      ))}
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const roundLabel = t('bracket.scoreModal.roundNo', { n: rowIndex + 1 });
                  return (
                    // 행은 순서가 곧 라운드 번호라 index 를 key 로 쓴다
                    <tr key={rowIndex}>
                      <th scope="row" className="score-rounds-round">
                        {roundLabel}
                      </th>
                      {row.sides.map((side, sideIndex) =>
                        FIELDS.map((field) => (
                          <td key={`${sideIndex}-${field}`}>
                            <input
                              className={`form-input${field === 'total' ? ' score-total-input' : ''}`}
                              inputMode="numeric"
                              aria-label={`${playerName(players[sideIndex])} ${roundLabel} ${
                                fieldLabels[field]
                              }`}
                              value={side[field]}
                              onChange={(e) => updateCell(rowIndex, sideIndex, field, e.target.value)}
                            />
                          </td>
                        ))
                      )}
                      <td>
                        <button
                          type="button"
                          className="score-round-remove"
                          onClick={() => handleRemoveRound(rowIndex)}
                          aria-label={t('bracket.scoreModal.removeRound', { n: rowIndex + 1 })}
                          title={t('bracket.scoreModal.removeRound', { n: rowIndex + 1 })}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" className="score-rounds-round">
                    {t('bracket.scoreModal.sum')}
                  </th>
                  {players.map((player, sideIndex) => (
                    <Fragment key={getPlayerId(player)}>
                      {FIELDS.map((field) => (
                        <td
                          key={field}
                          className={field === 'total' ? 'score-rounds-sum-total' : undefined}
                        >
                          {sumColumn(rows, sideIndex, field)}
                        </td>
                      ))}
                    </Fragment>
                  ))}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <button
            type="button"
            className="btn btn-add-round"
            onClick={handleAddRound}
            disabled={rows.length >= MAX_ROUNDS}
          >
            {t('bracket.scoreModal.addRound')}
          </button>

          <p className="score-hint">{t('bracket.scoreModal.hint')}</p>
          <label className="score-winner-option score-winner-none">
            <input
              type="radio"
              name="match-winner"
              checked={winnerIndex == null}
              onChange={() => setWinnerIndex(null)}
            />
            {t('bracket.scoreModal.noWinner')}
          </label>
          {allowDraw && (
            <label className="score-winner-option score-winner-none">
              <input
                type="radio"
                name="match-winner"
                checked={winnerIndex === DRAW}
                onChange={() => setWinnerIndex(DRAW)}
              />
              {t('bracket.scoreModal.draw')}
            </label>
          )}
          {error && <p className="admin-gate-error">{error}</p>}

          <div className="modal-footer">
            <button type="button" className="btn btn-reset-bracket" onClick={handleClearScores}>
              {t('bracket.scoreModal.clearScores')}
            </button>
            <button type="button" className="btn btn-cancel-edit" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-save-bracket">
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MatchScoreModal;
